import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { withFirebaseErrorHandling, retryFirebaseOperation } from '../errors/firebaseErrors';
import { TestMetadata, TEST_METADATA_COLLECTION } from '../../types/testMetadata';

export class TestMetadataService {
  private static COLLECTION_NAME = TEST_METADATA_COLLECTION;
  
  /**
   * Create a document key for test metadata
   * Format: tenantId_testId_userId (matches AnswerDataService pattern)
   */
  private static createDocumentKey(tenantId: number | string, userId: number | string, testId: string): string {
    // Ensure tenantId is never undefined
    const safeTenantId = tenantId || '50000';
    // Ensure userId is never undefined
    const safeUserId = userId || 'unknown';

    // Include userId to support multiple users solving the same test
    return `${safeTenantId}_${testId}_${safeUserId}`;
  }
  
  /**
   * Save or update test metadata in Firestore
   */
  static async saveTestMetadata(metadata: TestMetadata): Promise<void> {
    return retryFirebaseOperation(async () => {
      const docKey = this.createDocumentKey(metadata.tenantId, metadata.userId, metadata.testId);
      const docRef = doc(db, this.COLLECTION_NAME, docKey);
      
      // Ensure all required fields are included
      const dataToSave = {
        ...metadata,
        tenantId: metadata.tenantId || '50000', // Explicitly include tenantId
        lastActiveQuestionId: metadata.lastActiveQuestionId || '', // Default to empty string
        lastActiveQuestionIndex: metadata.lastActiveQuestionIndex ?? 0, // Default to 0 (use ?? for 0 values)
        defaultAnswerType: metadata.defaultAnswerType || 2, // Default to 2 if not specified
        status: metadata.status || 'in_progress', // Default status
        createdAt: metadata.createdAt || serverTimestamp(), // Only set on first create
        updatedAt: serverTimestamp()
      };

      await setDoc(docRef, dataToSave, { merge: true });
      
    }, 3, 'saveTestMetadata');
  }
  
  /**
   * Get test metadata from Firestore
   * Can be called with 2 or 3 parameters
   */
  static async getTestMetadata(
    testIdOrUserId: string | number,
    tenantId: number | string,
    testId?: string
  ): Promise<TestMetadata | null> {
    try {
      // Support both calling patterns
      let docKey: string;

      if (testId) {
        // 3 parameter version: (userId, tenantId, testId)
        const userId = testIdOrUserId;
        docKey = this.createDocumentKey(tenantId, userId, testId);
      } else {
        // 2 parameter version: (testId, tenantId) - for bulk sync compatibility
        const actualTestId = testIdOrUserId as string;
        // IMPORTANT: Cannot create proper key without userId
        // This version should not be used for user-specific metadata
        console.warn('⚠️ getTestMetadata called without userId - this may not work correctly for multi-user scenarios');
        docKey = `${tenantId}_${actualTestId}`;
      }

      const docRef = doc(db, this.COLLECTION_NAME, docKey);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data() as TestMetadata;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting test metadata from Firestore:', error);
      return null;
    }
  }
  
  /**
   * Update specific fields in test metadata
   */
  static async updateTestMetadata(
    userId: number | string,
    tenantId: number | string,
    testId: string,
    updates: Partial<TestMetadata>
  ): Promise<void> {
    return retryFirebaseOperation(async () => {
      const docKey = this.createDocumentKey(tenantId, userId, testId);
      const docRef = doc(db, this.COLLECTION_NAME, docKey);
      
      await setDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
      }, { merge: true });
    }, 3, 'updateTestMetadata');
  }
}